import React, { useState, useEffect, useCallback } from 'react'
import { Message } from 'rsuite'
import SelectableDatasetList from '../../../comp_reusable/lists/SelectableDatasetList'
import useStandardToaster from '../../../ui_reusable/StandardToaster'

const PAGE_SIZE = 8

const ClientInternalDriverList = ({ clientId, disabled = false }) => {
  const [loading, setLoading] = useState(true)
  const [internalDrivers, setInternalDrivers] = useState([])
  const [total, setTotal] = useState(0)
  const [selected, setSelected] = useState(0)
  const [page, setPage] = useState(1) // 1-based, matches `?page=` API contract
  const [search, setSearch] = useState('')
  const [sort, setSort] = useState(null) // { key (backend field), direction } | null
  const [showSelectedOnly, setShowSelectedOnly] = useState(false)
  const [refresh, setRefresh] = useState(new Date().getTime())
  const { pushToast } = useStandardToaster()

  const buildListParams = useCallback(
    ({ page: pageNum, limit }) => {
      const params = new URLSearchParams({
        scope: 'client',
        limit: String(limit),
        page: String(pageNum),
      })
      if (search) {
        params.set('search', search)
      }
      if (showSelectedOnly) {
        params.set('is_active', 'true')
      }
      if (sort) {
        params.set(
          'ordering',
          `${sort.direction === 'desc' ? '-' : ''}${sort.key}`,
        )
      }
      return params
    },
    [search, sort, showSelectedOnly],
  )

  useEffect(() => {
    const abortController = new AbortController()

    const fetchData = async () => {
      setLoading(true)
      try {
        const params = buildListParams({ page, limit: PAGE_SIZE })
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/datasets?${params.toString()}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
            },
            signal: abortController.signal,
          },
        )

        // DRF 404s on out-of-range pages — step back and let the effect refire.
        if (response.status === 404 && page > 1) {
          setPage(page - 1)
          return
        }

        const driversData = await response.json()

        setInternalDrivers(driversData.data || [])
        setTotal(driversData.total ?? driversData.data?.length ?? 0)
        setSelected(driversData.meta?.active ?? 0)
      } catch (error) {
        if (error.name !== 'AbortError') {
          pushToast(<Message type="error">Failed to load drivers</Message>, {
            placement: 'topCenter',
            duration: 5000,
          })
          setInternalDrivers([])
        }
      } finally {
        if (!abortController.signal.aborted) {
          setLoading(false)
        }
      }
    }

    fetchData()
    /* eslint-disable-next-line */
  }, [clientId, refresh, page, search, sort, buildListParams])

  const handlePageChange = useCallback((nextPage) => {
    setPage(nextPage)
  }, [])

  const handleSearchChange = useCallback((term) => {
    setPage(1)
    setSearch(term)
  }, [])

  const handleShowSelectedChange = useCallback((next) => {
    setPage(1)
    setShowSelectedOnly(next)
  }, [])

  // The grid emits its own column key; translate to the backend ordering field.
  const handleSortChange = useCallback((nextSort) => {
    setPage(1)
    setSort(
      nextSort && nextSort.key === 'name'
        ? { ...nextSort, key: 'dataset__name' }
        : nextSort,
    )
  }, [])

  // Toggle a single driver
  const handleToggleDriver = async (driverId, newState) => {
    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/datasets/${driverId}`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        method: 'PATCH',
        body: JSON.stringify({ is_active: newState }),
      },
    )

    if (!response.ok) {
      throw new Error('Failed to update driver selection')
    }

    // Update local state optimistically
    setInternalDrivers((prev) =>
      prev.map((driver) => {
        if (driver.dataset.id === driverId) {
          return {
            ...driver,
            is_active: newState,
          }
        }

        return driver
      }),
    )
    setSelected((count) => count + (newState ? 1 : -1))
  }

  // Toggle multiple drivers at once (current page or a provided set)
  const handleToggleMultipleDrivers = async (drivers, targetState) => {
    const updatePromises = drivers.map((driver) => {
      if (driver.is_active !== targetState) {
        return fetch(
          `${process.env.REACT_APP_API_URL}/api/clients/${clientId}/datasets/${driver.dataset.id}`,
          {
            headers: {
              Authorization: `Bearer ${localStorage.getItem('token')}`,
              'Content-Type': 'application/json',
            },
            method: 'PATCH',
            body: JSON.stringify({ is_active: targetState }),
          },
        )
      }

      return Promise.resolve()
    })

    await Promise.all(updatePromises)

    const changed = drivers.filter((driver) => driver.is_active !== targetState)
    setInternalDrivers((prev) =>
      prev.map((driver) => {
        if (changed.some((item) => item.dataset.id === driver.dataset.id)) {
          return {
            ...driver,
            is_active: targetState,
          }
        }

        return driver
      }),
    )
    setSelected(
      (count) => count + (targetState ? changed.length : -changed.length),
    )
  }

  // Select/deselect every matching driver under current search (all pages) — one API call.
  const handleSelectAllToggle = useCallback(async () => {
    const allSelected = total > 0 && selected === total
    const targetState = !allSelected

    const response = await fetch(
      `${process.env.REACT_APP_API_URL}/api/v2/clients/${clientId}/datasets/bulk-active/`,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
        method: 'POST',
        body: JSON.stringify({
          is_active: targetState,
          scope: 'client',
          search: search || '',
        }),
      },
    )
    if (!response.ok) {
      throw new Error('Failed to update driver selection')
    }
    setRefresh(new Date().getTime())
    return targetState
  }, [clientId, total, selected, search])

  // Function to refresh data
  const handleRefresh = () => {
    setRefresh(new Date().getTime())
  }

  return (
    <SelectableDatasetList
      clientId={clientId}
      data={internalDrivers}
      loading={loading}
      setLoading={setLoading}
      onToggleDriver={handleToggleDriver}
      onToggleMultipleDrivers={handleToggleMultipleDrivers}
      onSelectAllToggle={handleSelectAllToggle}
      onRefresh={handleRefresh}
      serverSide
      serverTotal={total}
      serverSelected={selected}
      serverPage={page}
      onPageChange={handlePageChange}
      onSearchChange={handleSearchChange}
      onSortChange={handleSortChange}
      showSelectedOnly={showSelectedOnly}
      onShowSelectedChange={handleShowSelectedChange}
      serverSortableKeys={['name']}
      serverSearchableKeys={['name']}
      selectionDisabled={disabled}
    />
  )
}

export default ClientInternalDriverList
